import type { ReactNode } from 'react'
import { AnimatedTitle } from '@/components/animated-title'
import { AppearanceToggle } from '@/components/appearance-toggle'
import { AuthBrandMark } from '@/components/auth/auth-brand-mark'
import { AuthMarketingIntro } from '@/components/auth/auth-marketing-intro'
import {
  ClientBackground,
  getGuestSurfaceBackgroundClassName,
} from '@/components/backgrounds/client-background'
import { getAuthMarketingShellConfig } from '@/constants/auth-marketing-shell'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { CURRENT_CLIENT } from '@/constants'
import { cn } from '@/lib/utils'

interface AuthPageShellProps {
  children: ReactNode
  title?: string
}

export function AuthPageShell({ children, title }: AuthPageShellProps) {
  const appTagline = (CURRENT_CLIENT as { tagline?: string }).tagline
  const marketingShell = getAuthMarketingShellConfig()
  const useMarketingShell = marketingShell !== null
  const shellClassName = marketingShell?.shellClassName
  const { guestTheme, guestPreference, setGuestTheme, themeToggleEnabled } =
    useGuestSurfaceTheme()

  return (
    <div
      className={cn(
        'h-full min-h-0 w-full overflow-x-hidden overflow-y-auto overscroll-y-contain lg:overflow-hidden',
        useMarketingShell
          ? 'bg-[var(--marketing-bg,var(--background))] text-[var(--marketing-text,var(--foreground))]'
          : 'bg-background',
        shellClassName
      )}
    >
      <div className="relative min-h-full w-full lg:h-full lg:min-h-0">
        {!useMarketingShell ? (
          <ClientBackground
            variant="auth"
            className={getGuestSurfaceBackgroundClassName('w-full')}
          />
        ) : null}

        {themeToggleEnabled ? (
          <div className="pointer-events-auto absolute right-4 top-4 z-20 sm:right-6 lg:right-12 2xl:right-20">
            <AppearanceToggle
              variant="marketing"
              marketingTheme={guestTheme}
              marketingPreference={guestPreference}
              onMarketingThemeChange={setGuestTheme}
            />
          </div>
        ) : null}

        <div className="relative z-10 grid min-h-full w-full grid-cols-1 items-start gap-2 px-4 py-8 sm:gap-3 sm:px-6 sm:py-10 lg:h-full lg:min-h-0 lg:grid-cols-2 lg:items-stretch lg:gap-12 lg:px-12 lg:py-0 2xl:px-20">
          <section className="flex flex-col items-center text-center lg:h-full lg:items-start lg:justify-center lg:text-left">
            <div className="pb-1">
              {useMarketingShell && marketingShell?.brandMark ? (
                <AuthBrandMark markKey={marketingShell.brandMark} size="md" />
              ) : (
                <AnimatedTitle
                  logoOnLightBackground
                  navigateHome
                  logoClassName="inline-block h-8 aspect-[123/27] bg-foreground"
                />
              )}
            </div>
            {useMarketingShell ? (
              <AuthMarketingIntro
                eyebrow={marketingShell?.eyebrow}
                tagline={marketingShell?.tagline}
              />
            ) : appTagline ? (
              <p className="max-w-xl text-lg font-semibold leading-tight text-foreground sm:text-xl">
                {appTagline}
              </p>
            ) : null}
          </section>

          <section className="flex w-full flex-col items-center pb-8 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:py-6 2xl:py-8 custom-scrollbar">
            <div
              className={cn(
                'w-full max-w-md px-6 pb-6 pt-9 shadow-lg backdrop-blur-md lg:my-auto lg:pb-5 lg:pt-6 [&_[data-slot=input]]:h-12 [&_button:not(.h-auto)]:h-12',
                useMarketingShell
                  ? 'rounded-[var(--radius-marketing-card,28px)] border border-[var(--marketing-border,#e9e3d6)] bg-[var(--marketing-card,#ffffff)]/95'
                  : 'rounded-xl border border-border/50 bg-background/80'
              )}
            >
              {title ? (
                <h1
                  className={cn(
                    'mb-6 text-center text-xl font-semibold leading-tight',
                    useMarketingShell ? 'text-[var(--marketing-text,#1f2528)]' : 'text-foreground'
                  )}
                >
                  {title}
                </h1>
              ) : null}
              {children}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
